// dependencies
import * as Sentry from "@sentry/react";
import React, { useState } from "react";

// icons
import { faTriangleExclamation } from "@performyard/fontawesome/pro-solid-svg-icons";
import { PageBanner } from "@performyard/py-components";

import { AdminContainer, AdminTitle } from "../../../components/Admin";
//components
import { Grid, GridCol } from "../../../components/Grid";
import Toggle from "../../../components/Inputs/ToggleSwitches/ThumbSwitch";
import PageTitle from "../../../components/PageTitle";
import Spacer from "../../../components/Spacer";
import Text from "../../../components/Text";

// hooks
import { useAiSettings, useUpdateAiSettings } from "../../../api/hooks";
import { getFeatureFlagQuery, useFeatureFlagQuery } from "../../../hooks/useFeatureFlag";
import useToast from "../../../hooks/useToast";

import { companyAISettingsQuery, currentUserQuery } from "../../../api/queries";
import { dataLoader } from "../../../api/queries/dataLoader";

// utils
import { showError } from "../../../utils/RequestReponse";

// pages
import ErrorPage from "../../Error";

/** Data loader function */
export const conversationalAISettingsLoader = () => {
	const queries = [companyAISettingsQuery, getFeatureFlagQuery];

	return dataLoader([[currentUserQuery], queries]);
};

/** Page content */
const ConversationalAISettings = () => {
	const { mutateAsync: updateAiSettings } = useUpdateAiSettings();
	const { data } = useAiSettings();
	// AI feature flags
	const { value: aiFeatureFlagEnabled } = useFeatureFlagQuery("ff-is-ai-enabled", false);
	const { value: conversationalAIFeatureFlagEnabled } = useFeatureFlagQuery("ff-is-conversational-ui-enabled", false);

	const toast = useToast();

	const [enabled, setEnabled] = useState(data?.conversationalAiEnabled);
	const [showDocumentBanner, setShowDocumentBanner] = useState(false);
	const [hasSeenBannerThisSession, setHasSeenBannerThisSession] = useState(false);

	// toggles behave differently in that changes are saved without having to click the save button
	const handleEnabledToggle = async (val: boolean) => {
		try {
			await updateAiSettings({ conversationalAiEnabled: val });
			setEnabled(val);

			// Show banner when enabling (if not seen this session), hide when disabling
			if (val && !hasSeenBannerThisSession) {
				setShowDocumentBanner(true);
				setHasSeenBannerThisSession(true);
			} else if (!val) {
				setShowDocumentBanner(false);
				setHasSeenBannerThisSession(false); // Reset so banner can appear again when turned back on
			}

			toast(`Ask PerformYard ${val === true ? "Enabled" : "Disabled"}.`, {
				type: "success",
				size: "large",
			});
		} catch {
			showError("Unable to save settings.");
		}
	};

	const handleDismissBanner = () => {
		setShowDocumentBanner(false);
		setHasSeenBannerThisSession(true);
	};

	if (!aiFeatureFlagEnabled || !conversationalAIFeatureFlagEnabled) {
		return <ErrorPage code={404} />;
	}

	return (
		<Sentry.ErrorBoundary fallback={undefined}>
			<Spacer height={4} />
			<AdminContainer>
				<PageTitle title="Ask PerformYard Settings" />
				<AdminTitle>Ask PerformYard</AdminTitle>
				<Spacer height={4} />
				<Grid cols={10} gap={2}>
					{/* left side of the page */}
					<GridCol span={4}>
						<Text size="paragraph-2" weight="semi-bold" color="neutral-9">
							Ask PerformYard Settings
						</Text>
						<Spacer height={1} />
						<div className="mb-4">
							<Text size="paragraph-3" weight="regular" color="neutral-9">
								Control whether or not the members of your organization can access and utilize Ask
								PerformYard.
							</Text>
						</div>
						<Spacer height={3} />
					</GridCol>
					<GridCol span={1} />

					{/* right side of the page */}
					<GridCol span={5}>
						<div className="grid grid-cols-5">
							{/* enable conversational AI toggle */}
							<div className="col-span-3">
								<Text size="paragraph-2" weight="semi-bold" color="neutral-9">
									Enable Ask PerformYard Functionality
								</Text>
								<div className="h-2" />
								<div className="mb-2">
									<Text size="paragraph-3" weight="regular" color="neutral-9">
										Disabling this setting will hide the ability for users to utilize Ask
										PerformYard.
									</Text>
								</div>
							</div>

							<div className="col-span-2">
								<div data-testid="enable-toggle" className="flex items-center justify-end">
									<div className="flex items-center">
										<Toggle onChange={(val) => handleEnabledToggle(val)} value={enabled || false} />
									</div>
								</div>
							</div>
						</div>

						{/* Document management banner */}
						{showDocumentBanner && (
							<>
								<div className="h-2" />
								<PageBanner
									color="primary"
									icon={faTriangleExclamation}
									mainTitle="Manage Documents"
									subTitle="We’ve enabled a set of documents from your Company Resources to help you get started with  Ask PerformYard. You can review and manage document access at any time, or upload new additional ones to tailor the feature to your organization’s needs."
									bottomActionButton={{
										children: "Go to Company Resources",
										href: "/company/resources",
										color: "primary",
									}}
									onDismiss={handleDismissBanner}
								/>
							</>
						)}
					</GridCol>
				</Grid>
			</AdminContainer>
		</Sentry.ErrorBoundary>
	);
};

export default ConversationalAISettings;
